import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  HostBinding,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { preparePortConsumptionReportForAnalysis } from 'src/app/shared/helpers/document-pdf.util';
import { formatExecutiveSummary } from 'src/app/shared/utils/vessel-insights-format.util';
import {
  BenchmarkComparisonView,
  benchmarkBarWidth,
  formatVarianceLabel,
  InsightTagView,
  ScoreBreakdownItem,
} from 'src/app/shared/utils/vessel-insights-metrics.util';
import { normalizeVesselInsightsPayload } from 'src/app/shared/utils/vessel-insights-normalize.util';
import { downloadAiAnalysisPdf } from 'src/app/shared/utils/vessel-insights-pdf.util';
import { buildVesselInsightsPrompt } from 'src/app/shared/utils/vessel-insights-prompt.util';
import { PortRequestFormServiceService } from 'src/app/shared/service/port-request-FormService/port-request-form.service.service';
import { RequestFormService } from 'src/app/shared/service/requestForm/request-form.service';

export interface ExpertCommentView {
  role: string;
  comment: string;
  fullComment: string;
  needsReadMore: boolean;
  icon: string;
  accent: string;
  linkClass: string;
}

export interface RiskIndicatorView {
  label: string;
  percent: number;
  level: 'low' | 'medium' | 'high';
  filledSegments: number;
  animatedPercent?: number;
}

export interface KpiItemView {
  icon: string;
  value: string;
  label: string;
}

export interface TypingFindingView {
  full: string;
  visible: string;
  complete: boolean;
}

export interface LoadingStepView {
  icon: string;
  label: string;
  state: 'pending' | 'active' | 'completed';
}

@Component({
  selector: 'app-gemininotes-popup',
  templateUrl: './gemininotes-popup.component.html',
  styleUrls: ['./gemininotes-popup.component.scss'],
})
export class GemininotesPopupComponent implements OnInit, OnDestroy {
  loading = true;
  loadingExiting = false;
  errorMessage = '';
  closing = false;
  contentRevealed = false;

  aiInsights: string[] = [];
  guidance: string[] = [];
  expertComments: ExpertCommentView[] = [];
  typingFindings: TypingFindingView[] = [];

  executiveSummary = '';
  operationalScore = 82;
  scoreLabel = 'Good';
  aiConfidence = 94;
  generatedLabel = 'Just now';

  displayedConfidence = 0;
  displayedScore = 0;
  scoreRingDeg = 0;
  scoreRingComplete = false;

  kpis: KpiItemView[] = [];
  insightTags: InsightTagView[] = [];
  riskIndicators: RiskIndicatorView[] = [];
  benchmark: BenchmarkComparisonView | null = null;
  scoreBreakdown: ScoreBreakdownItem[] = [];
  scoreReasons: string[] = [];
  showScoreExplain = false;
  expertModal: ExpertCommentView | null = null;
  confidenceHint = '';

  private readonly loadingStepDefs = [
    { icon: 'description', label: 'Extracting consumption pages...' },
    { icon: 'menu_book', label: 'Reading PDF report...' },
    { icon: 'bar_chart', label: 'Analyzing benchmark data...' },
    { icon: 'groups', label: 'Generating expert commentary...' },
    { icon: 'speed', label: 'Calculating operational score...' },
    { icon: 'track_changes', label: 'Building recommendations...' },
  ];

  loadingSubMessage = 'Extracting and analyzing port consumption report...';
  loadingSteps: LoadingStepView[] = [];
  readonly particleSeeds = Array.from({ length: 14 }, (_, i) => i);
  readonly loadingWaveSeeds = Array.from({ length: 6 }, (_, i) => i);

  private loadStartedAt = Date.now();
  private reportBlob: Blob | null = null;
  private extractedReportContext = '';
  private loadingProgressTimer: ReturnType<typeof setInterval> | null = null;
  private typingTimers: ReturnType<typeof setTimeout>[] = [];
  private rafId: number | null = null;

  private readonly roleMeta: Record<
    string,
    { icon: string; accent: string; linkClass: string }
  > = {
    'Chief Engineer': {
      icon: 'engineering',
      accent: '#34d399',
      linkClass: 'link--green',
    },
    'Technical Superintendent': {
      icon: 'supervisor_account',
      accent: '#fb923c',
      linkClass: 'link--orange',
    },
    'Naval Architect': {
      icon: 'architecture',
      accent: '#60a5fa',
      linkClass: 'link--blue',
    },
  };

  @HostBinding('class.ai-insights-host--closing')
  get hostClosing(): boolean {
    return this.closing;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<GemininotesPopupComponent>,
    private portConsumptionService: PortRequestFormServiceService,
    private requestFormService: RequestFormService,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer,
  ) {
    this.dialogRef.disableClose = false;
    this.resetLoadingSteps();
  }

  ngOnInit(): void {
    this.resetLoadingSteps();
    this.loadInsights();
  }

  ngOnDestroy(): void {
    this.stopLoadingProgress();
    this.clearTypingTimers();
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
    }
  }

  get prefersReducedMotion(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  get vesselDisplayName(): string {
    const name = this.data?.vessel_name || '';
    return name ? String(name).toUpperCase() : '—';
  }

  get vesselImo(): string {
    return this.data?.vessel_imo ? `IMO: ${this.data.vessel_imo}` : 'IMO: —';
  }

  get activeLoadingStepNumber(): number {
    const idx = this.loadingSteps.findIndex((step) => step.state === 'active');
    return idx >= 0 ? idx + 1 : this.loadingSteps.length;
  }

  get scoreRingStyle(): Record<string, string> {
    return {
      background: `conic-gradient(from -90deg, #7c3aed 0deg, #3b82f6 ${this.scoreRingDeg}deg, rgba(51, 65, 85, 0.45) ${this.scoreRingDeg}deg 360deg)`,
    };
  }

  formatInsightHtml(text: string): SafeHtml {
    let html = this.escapeHtml(String(text ?? ''));

    const vessel = this.data?.vessel_name;
    if (vessel) {
      html = this.wrapMatches(html, vessel, 'accent-vessel');
    }

    const port = this.portName;
    if (port) {
      html = this.wrapMatches(html, port, 'accent-port');
    }

    html = html.replace(
      /\b(IMO\s*#?\s*\d{7}|\d{7})\b/gi,
      '<span class="accent-imo">$1</span>',
    );
    html = html.replace(
      /\b(\d{1,2}[\/.-]\d{1,2}[\/.-]\d{2,4}|\d{4}-\d{2}-\d{2}|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{1,2},?\s+\d{4})/gi,
      '<span class="accent-date">$1</span>',
    );
    html = html.replace(
      /\b(\d+(?:\.\d+)?)\s*(MT|days?|hrs?|hours?)\b/gi,
      '<span class="accent-value">$1 $2</span>',
    );

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  toggleScoreExplain(): void {
    this.showScoreExplain = !this.showScoreExplain;
  }

  openExpertModal(c: ExpertCommentView): void {
    this.expertModal = c;
  }

  closeExpertModal(): void {
    this.expertModal = null;
  }

  formatMt(value: number | null, variancePct: number | null = null): string {
    if (value != null) {
      return `${value} MT`;
    }
    if (variancePct != null) {
      return `Est. from ${variancePct > 0 ? '+' : ''}${variancePct}% var.`;
    }
    return 'N/A';
  }

  formatBenchmarkVariance(
    pct: number | null,
    mt: number | null,
  ): string {
    return formatVarianceLabel(pct, mt);
  }

  get benchmarkChartMax(): number {
    if (!this.benchmark) {
      return 0;
    }
    const values = [
      this.benchmark.actualMt,
      this.benchmark.tcpMt,
      this.benchmark.baselineMt,
    ].filter((value): value is number => value != null);
    return values.length ? Math.max(...values) : 0;
  }

  getBenchmarkBarWidth(value: number | null): number {
    return benchmarkBarWidth(value, this.benchmarkChartMax);
  }

  close(): void {
    if (this.closing) return;
    if (this.prefersReducedMotion) {
      this.dialogRef.close();
      return;
    }
    this.closing = true;
    setTimeout(() => this.dialogRef.close(), 320);
  }

  getRiskSegmentsOn(r: RiskIndicatorView): number {
    const pct = r.animatedPercent ?? 0;
    return Math.min(4, Math.max(0, Math.ceil(pct / 25)));
  }

  downloadReport(): void {
    if (!this.reportBlob) {
      this.toastr.warning('Report file is not available.');
      return;
    }
    const url = URL.createObjectURL(this.reportBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.data?.vessel_name || 'vessel'}-port-consumption-report.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  }

  downloadAiAnalysis(): void {
    if (!this.benchmark || !this.executiveSummary) {
      this.toastr.warning('AI analysis is not ready yet.');
      return;
    }

    const op =
      this.kpis.find((k) => k.label === 'Operation Type')?.value || '—';

    downloadAiAnalysisPdf({
      vesselName: this.data?.vessel_name || 'Vessel',
      imo: String(this.data?.vessel_imo ?? '—'),
      port: this.portName,
      operation: op,
      generatedAt: this.generatedLabel,
      executiveSummary: this.executiveSummary,
      aiInsights: this.aiInsights,
      guidance: this.guidance,
      chiefEngineer:
        this.expertComments.find((c) => c.role === 'Chief Engineer')
          ?.fullComment ?? '—',
      technicalSuperintendent:
        this.expertComments.find((c) => c.role === 'Technical Superintendent')
          ?.fullComment ?? '—',
      navalArchitect:
        this.expertComments.find((c) => c.role === 'Naval Architect')
          ?.fullComment ?? '—',
      operationalScore: this.operationalScore,
      aiConfidence: this.aiConfidence,
      benchmark: this.benchmark,
      scoreBreakdown: this.scoreBreakdown,
      riskIndicators: this.riskIndicators.map((r) => ({
        label: r.label,
        percent: r.percent,
      })),
    });
  }

  private get portName(): string {
    const p = this.data?.resolved_port_name;
    if (Array.isArray(p)) return p[0] || '—';
    return p || '—';
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private escapeRegex(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private wrapMatches(html: string, term: string, className: string): string {
    if (!term?.trim()) return html;
    const re = new RegExp(`(${this.escapeRegex(term)})`, 'gi');
    return html.replace(re, `<span class="${className}">$1</span>`);
  }

  private createLoadingSteps(activeIndex: number): LoadingStepView[] {
    const defs = this.loadingStepDefs ?? [];
    return defs.map((step, index) => ({
      icon: step.icon,
      label: step.label,
      state:
        index < activeIndex
          ? 'completed'
          : index === activeIndex
            ? 'active'
            : 'pending',
    }));
  }

  private resetLoadingSteps(): void {
    this.loadingSteps = this.createLoadingSteps(0);
    this.loadingSubMessage =
      'Extracting and analyzing port consumption report...';
  }

  private setLoadingStep(
    stepIndex: number,
    subMessage?: string,
  ): void {
    this.loadingSteps = this.createLoadingSteps(
      Math.min(stepIndex, this.loadingStepDefs.length - 1),
    );
    if (subMessage) {
      this.loadingSubMessage = subMessage;
    }
  }

  private completeAllLoadingSteps(): void {
    this.loadingSteps = this.loadingStepDefs.map((step) => ({
      icon: step.icon,
      label: step.label,
      state: 'completed' as const,
    }));
    this.loadingSubMessage = 'Analysis complete. Preparing dashboard...';
  }

  private startLoadingProgressDuringApi(): void {
    this.stopLoadingProgress();
    let tick = 0;
    this.loadingProgressTimer = setInterval(() => {
      tick += 1;
      if (tick === 2) {
        this.setLoadingStep(4, 'Calculating operational score from benchmarks...');
      } else if (tick >= 4) {
        this.setLoadingStep(5, 'Building actionable recommendations...');
        this.stopLoadingProgress();
      }
    }, 2800);
  }

  private stopLoadingProgress(): void {
    if (this.loadingProgressTimer) {
      clearInterval(this.loadingProgressTimer);
      this.loadingProgressTimer = null;
    }
  }

  private clearTypingTimers(): void {
    this.typingTimers.forEach(clearTimeout);
    this.typingTimers = [];
  }

  private finishLoading(): void {
    this.stopLoadingProgress();

    if (this.errorMessage) {
      this.loading = false;
      this.loadingExiting = false;
      return;
    }

    this.completeAllLoadingSteps();
    this.loadingExiting = true;

    setTimeout(() => {
      this.loading = false;
      this.loadingExiting = false;
      requestAnimationFrame(() => this.runRevealAnimations());
    }, this.prefersReducedMotion ? 0 : 520);
  }

  private runRevealAnimations(): void {
    this.contentRevealed = true;

    if (this.prefersReducedMotion) {
      this.displayedConfidence = this.aiConfidence;
      this.displayedScore = this.operationalScore;
      this.scoreRingDeg = (this.operationalScore / 100) * 360;
      this.scoreRingComplete = true;
      this.riskIndicators = this.riskIndicators.map((r) => ({
        ...r,
        animatedPercent: r.percent,
      }));
      this.typingFindings = this.aiInsights.map((full) => ({
        full,
        visible: full,
        complete: true,
      }));
      return;
    }

    this.animateValue(0, this.aiConfidence, 1100, (v) => {
      this.displayedConfidence = Math.round(v);
    });

    this.animateValue(0, this.operationalScore, 1400, (v) => {
      const rounded = Math.round(v);
      this.displayedScore = rounded;
      this.scoreRingDeg = (v / 100) * 360;
    });
    setTimeout(() => {
      this.scoreRingComplete = true;
    }, 1500);

    this.riskIndicators = this.riskIndicators.map((r) => ({
      ...r,
      animatedPercent: 0,
    }));
    this.riskIndicators.forEach((r, i) => {
      setTimeout(
        () => {
          this.animateValue(0, r.percent, 900, (v) => {
            r.animatedPercent = Math.round(v);
          });
        },
        400 + i * 120,
      );
    });

    this.startTypingFindings();
  }

  private animateValue(
    from: number,
    to: number,
    durationMs: number,
    onUpdate: (v: number) => void,
  ): void {
    const start = performance.now();
    const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

    const step = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      onUpdate(from + (to - from) * easeOut(t));
      if (t < 1) {
        this.rafId = requestAnimationFrame(step);
      }
    };
    this.rafId = requestAnimationFrame(step);
  }

  private startTypingFindings(): void {
    this.clearTypingTimers();
    this.typingFindings = this.aiInsights.map((full) => ({
      full,
      visible: '',
      complete: false,
    }));

    this.typingFindings.forEach((item, index) => {
      const delay = 600 + index * 280;
      const timer = setTimeout(() => this.typeFinding(item), delay);
      this.typingTimers.push(timer);
    });
  }

  private typeFinding(item: TypingFindingView): void {
    const text = item.full;
    const chars = text.length;
    if (!chars) {
      item.complete = true;
      return;
    }
    const msPerChar = Math.max(12, Math.min(28, 1800 / chars));
    let i = 0;

    const tick = () => {
      i += 1;
      item.visible = text.slice(0, i);
      if (i < chars) {
        const t = setTimeout(tick, msPerChar);
        this.typingTimers.push(t);
      } else {
        item.complete = true;
      }
    };
    tick();
  }

  private normalizeInsightText(value: unknown, fallback = '—'): string {
    if (value == null) {
      return fallback;
    }
    const text = String(value).trim();
    return text || fallback;
  }

  private buildExpertComments(normalized: {
    chief_engineer: string;
    chief_engineer_full: string;
    technical_superintendent: string;
    technical_superintendent_full: string;
    naval_architect: string;
    naval_architect_full: string;
  }): ExpertCommentView[] {
    const rows = [
      {
        role: 'Chief Engineer',
        comment: normalized.chief_engineer,
        fullComment: normalized.chief_engineer_full,
      },
      {
        role: 'Technical Superintendent',
        comment: normalized.technical_superintendent,
        fullComment: normalized.technical_superintendent_full,
      },
      {
        role: 'Naval Architect',
        comment: normalized.naval_architect,
        fullComment: normalized.naval_architect_full,
      },
    ];
    return rows.map((row) => {
      const meta = this.roleMeta[row.role] ?? {
        icon: 'person',
        accent: '#a78bfa',
        linkClass: 'link--purple',
      };
      return {
        role: row.role,
        comment: row.comment,
        fullComment: row.fullComment,
        needsReadMore: row.fullComment !== row.comment,
        icon: meta.icon,
        accent: meta.accent,
        linkClass: meta.linkClass,
      };
    });
  }

  private computePortStayHours(): string {
    const eosp = this.data?.eosp_datetime;
    const cosp = this.data?.cosp_datetime;
    if (eosp && cosp) {
      const hrs =
        (new Date(cosp).getTime() - new Date(eosp).getTime()) / 3600000;
      if (hrs > 0 && hrs < 500) {
        return `${hrs.toFixed(1)} hrs`;
      }
    }
    for (const line of this.aiInsights) {
      const m = line.match(/(\d+(?:\.\d+)?)\s*(?:hours?|hrs?)/i);
      if (m) return `${m[1]} hrs`;
    }
    return '—';
  }

  private buildDashboardMeta(payload: any): void {
    const op =
      this.normalizeInsightText(payload.operation, '') ||
      this.data?.cargo_operation ||
      '—';
    const port =
      this.normalizeInsightText(payload.port, '') || this.portName;

    this.scoreLabel = this.scoreLabelFromValue(this.operationalScore);

    this.kpis = [
      { icon: 'schedule', value: this.computePortStayHours(), label: 'Port Stay Duration' },
      { icon: 'precision_manufacturing', value: op, label: 'Operation Type' },
      { icon: 'location_on', value: port, label: 'Port' },
      {
        icon: 'insights',
        value: `${this.operationalScore}%`,
        label: 'Operational Efficiency',
      },
    ];

    if (!this.executiveSummary) {
      this.executiveSummary = formatExecutiveSummary(
        `Performance at ${port} during ${op} reviewed against TCP and baseline benchmarks.`,
      );
    }

    const elapsed = Math.max(1, Math.round((Date.now() - this.loadStartedAt) / 1000));
    this.generatedLabel =
      elapsed < 60 ? `Generated ${elapsed} sec ago` : `Generated ${Math.round(elapsed / 60)} min ago`;
  }

  private scoreLabelFromValue(score: number): string {
    if (score >= 85) return 'Excellent';
    if (score >= 70) return 'Good';
    if (score >= 50) return 'Fair';
    return 'Poor';
  }

  get scoreLabelClass(): string {
    if (this.displayedScore >= 85) return 'score-gauge__label--excellent';
    if (this.displayedScore >= 70) return 'score-gauge__label--good';
    if (this.displayedScore >= 50) return 'score-gauge__label--fair';
    return 'score-gauge__label--poor';
  }

  private buildConfidenceHint(meta?: {
    pageCount: number;
    pagesIncluded: number[];
    textChars: number;
    usedOcr: boolean;
    markerCount: number;
  }): string {
    if (!meta) {
      return 'Confidence reflects extracted report data quality.';
    }
    const parts = [
      `${meta.pagesIncluded.length} consumption pages`,
      `${meta.textChars} text characters`,
      `${meta.markerCount} benchmark markers`,
    ];
    if (meta.usedOcr) {
      parts.push('OCR-assisted extraction');
    }
    return `Based on ${parts.join(', ')}.`;
  }

  private buildRisks(
    risks: Array<{ label: string; percent: number }>,
  ): RiskIndicatorView[] {
    return risks.map((risk) => this.normalizeRisk(risk.label, risk.percent));
  }

  private normalizeRisk(label: string, percent: number): RiskIndicatorView {
    const p = Math.min(100, Math.max(0, percent));
    const filledSegments = Math.min(4, Math.max(1, Math.round(p / 25)));
    let level: RiskIndicatorView['level'] = 'low';
    if (p >= 40) level = 'high';
    else if (p >= 25) level = 'medium';
    return { label, percent: p, level, filledSegments, animatedPercent: 0 };
  }

  private buildInsightsPrompt(extractedContext: string): string {
    return buildVesselInsightsPrompt(extractedContext);
  }

  private extractInsightsPayload(res: any): any | null {
    const data = res?.data ?? res;
    if (!data || typeof data !== 'object') {
      return null;
    }
    if (res?.status && res.status !== 'success' && res?.data == null) {
      return null;
    }
    return data;
  }

  private parseRawInsightsResponse(text: string): any | null {
    const cleaned = text.replace(/```json|```/g, '').replace(/^json\n?/, '').trim();
    try {
      return JSON.parse(cleaned);
    } catch {
      const lastBrace = cleaned.lastIndexOf('{');
      if (lastBrace >= 0) {
        try {
          return JSON.parse(cleaned.slice(lastBrace));
        } catch {
          return null;
        }
      }
      return null;
    }
  }

  private formatInsightsApiError(err: HttpErrorResponse): string {
    const body = err?.error;
    if (typeof body === 'string' && body.trim()) {
      return body.trim().slice(0, 240);
    }
    if (body && typeof body === 'object') {
      const msg = body.message || body.error || body.detail;
      if (msg != null) {
        return String(msg);
      }
    }
    if (err?.status === 0) {
      return 'Network error while analyzing the report.';
    }
    if (err?.status >= 500) {
      return 'AI analysis service is temporarily unavailable.';
    }
    if (String(err?.message || '').toLowerCase().includes('timeout')) {
      return 'AI analysis timed out. Please try again.';
    }
    return 'Failed to analyze the report. Please try again later.';
  }

  private applyInsightsPayload(
    payload: any,
    extractionMeta?: {
      pageCount: number;
      pagesIncluded: number[];
      textChars: number;
      usedOcr: boolean;
      markerCount: number;
    },
  ): void {
    if (!payload) {
      this.errorMessage = 'No insight data returned from the server.';
      return;
    }

    if (payload.raw_response) {
      const text = String(payload.raw_response);
      const parsed = this.parseRawInsightsResponse(text);
      if (parsed && typeof parsed === 'object') {
        payload = { ...payload, ...parsed };
      } else {
        payload = { raw_response: text, ai_insights: text };
      }
    }

    const normalized = normalizeVesselInsightsPayload(
      payload,
      this.extractedReportContext,
      extractionMeta,
    );
    this.aiInsights = normalized.ai_insights;
    this.guidance = normalized.guidance;
    this.executiveSummary = normalized.executive_summary;
    this.operationalScore = normalized.operational_score;
    this.aiConfidence = normalized.ai_confidence;
    this.riskIndicators = this.buildRisks(normalized.risk_indicators);
    this.expertComments = this.buildExpertComments(normalized);
    this.benchmark = normalized.benchmark;
    this.scoreBreakdown = normalized.score_breakdown;
    this.scoreReasons = normalized.score_reason;
    this.insightTags = normalized.insight_tags;
    this.scoreLabel = this.scoreLabelFromValue(normalized.operational_score);
    this.confidenceHint = this.buildConfidenceHint(extractionMeta);
    this.showScoreExplain = false;
    this.buildDashboardMeta(payload);
  }

  private async loadInsights(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';
    this.contentRevealed = false;
    this.displayedConfidence = 0;
    this.displayedScore = 0;
    this.scoreRingDeg = 0;
    this.loadStartedAt = Date.now();
    this.aiInsights = [];
    this.guidance = [];
    this.expertComments = [];
    this.typingFindings = [];
    this.benchmark = null;
    this.scoreBreakdown = [];
    this.scoreReasons = [];
    this.insightTags = [];
    this.scoreRingComplete = false;
    this.resetLoadingSteps();
    this.expertModal = null;
    this.showScoreExplain = false;

    const row = this.data;
    const s3Url = row?.port_consumption_report_s3_url;

    if (!s3Url) {
      this.errorMessage =
        'Report is still being generated. Insights will be available once the report is ready.';
      this.finishLoading();
      return;
    }

    try {
      this.setLoadingStep(1, 'Reading port consumption report...');
      const pdfBlob = await firstValueFrom(
        this.portConsumptionService.getReport({ s3_url: s3Url }),
      );
      this.reportBlob = pdfBlob;
      this.setLoadingStep(0, 'Extracting consumption pages from report...');
      const reportExtraction =
        await preparePortConsumptionReportForAnalysis(pdfBlob);
      this.extractedReportContext = reportExtraction.extractedContext;
      this.setLoadingStep(2, 'Analyzing benchmark and consumption data...');
      console.debug(
        '[AI Insights] pages extracted:',
        reportExtraction.pagesIncluded,
        'of',
        reportExtraction.pageCount,
        'image bytes:',
        reportExtraction.imageFile.size,
      );
      const prompt = this.buildInsightsPrompt(reportExtraction.extractedContext);
      this.setLoadingStep(3, 'Generating expert commentary and AI insights...');
      this.startLoadingProgressDuringApi();

      this.requestFormService
        .uploadVesselInsights(reportExtraction.imageFile, prompt)
        .subscribe({
        next: (res) => {
          try {
            if (res?.status === 'error') {
              this.errorMessage =
                this.normalizeInsightText(res?.message, '') ||
                'Failed to analyze the report. Please try again later.';
              this.finishLoading();
              this.toastr.error(this.errorMessage);
              return;
            }
            const payload = this.extractInsightsPayload(res);
            if (!payload) {
              this.errorMessage =
                this.normalizeInsightText(res?.message, '') ||
                'Could not load vessel insights.';
              this.finishLoading();
              return;
            }
            this.applyInsightsPayload(
              payload,
              reportExtraction.extractionMeta,
            );
            this.finishLoading();
          } catch (err) {
            console.error('[AI Insights] response processing failed', err);
            this.finishLoading();
            this.errorMessage =
              'Received AI insights but could not display them. Please try again.';
            this.toastr.error(this.errorMessage);
          }
        },
        error: (err: HttpErrorResponse) => {
          console.error('[AI Insights] API request failed', err);
          this.finishLoading();
          this.errorMessage = this.formatInsightsApiError(err);
          this.toastr.error(this.errorMessage);
        },
      });
    } catch (err: unknown) {
      this.finishLoading();
      const detail =
        err instanceof Error && err.message?.trim()
          ? err.message.trim()
          : 'Could not prepare the report image for analysis. Please try again.';
      this.errorMessage = detail;
      this.toastr.error(detail);
    }
  }
}
